import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@material-ui/core';
import StyledCard from '../../components/cards/StyledCard';
import StyledCardHeader, { HeaderButtonIcon } from '../../components/cards/StyledCardHeader';
import StyledCardContent from '../../components/cards/StyledCardContent';
import { NameValueRowList } from '../../components/cards/CardRows/NameValueRowList';
import { drawerStore } from '../../state/drawerStore';
import { FormInputTypes } from '../../utils/constants';
import { Api } from '../../api';

interface Props {
  chargePointId: string;
}

interface FinancingOption {
  value: string;
  label: string;
}

/**
 * NAP SI financing for a single charge point — Nacin / Prejemnik / Pogodba financiranja.
 *
 * The three fields cascade: a Prejemnik is only valid for certain Nacini, and a Pogodba
 * only for a given Nacin + Prejemnik pair. That filtering is done by the charge service,
 * one endpoint per level, so this component only asks for the next level's options once
 * its parent is chosen.
 *
 * Every field is optional. A row holding only Nacin is legitimate — that is what every
 * backfilled row looks like, since NAP SI does not expose Prejemnik or Pogodba on read.
 */
/**
 * Lets a level be set back to empty. Selecting it on Nacin clears all three fields,
 * mirroring the server-side rule that a cleared parent nulls its dependants.
 */
const CLEAR_OPTION: FinancingOption = { value: '', label: '— brez —' };

const withClear = (options: FinancingOption[]) => [CLEAR_OPTION, ...options];

const ChargerNapSiSettingsCard = observer(({ chargePointId }: Props) => {
  const [settings, setSettings] = useState<any | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const loadSettings = useCallback(async () => {
    const response = await Api.charge.getChargePointNapSiSettings(chargePointId);
    // The REST framework wraps payloads as { data: ... }; the endpoint itself returns
    // null when the charge point has no stored settings.
    setSettings(response.data.data ?? null);
  }, [chargePointId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onConfirmRemove = async () => {
    setConfirmRemoveOpen(false);
    await Api.charge.deleteChargePointNapSiSettings(chargePointId);
    await loadSettings();
  };

  const rows = [
    {
      name: 'napSiFinancedName',
      label: 'Način financiranja',
      value: settings?.napSiFinancedName || '—',
      // Only offered when there is something to remove. Clearing Nacin in the drawer
      // has the same effect; this is the direct route.
      actionLabel: settings ? 'remove' : undefined,
      actionFunction: settings ? () => setConfirmRemoveOpen(true) : undefined,
    },
    {
      name: 'napSiReceivingSupportCompanyName',
      label: 'Prejemnik financiranja',
      value: settings?.napSiReceivingSupportCompanyName || '—',
    },
    {
      name: 'napSiReceivingAgreementName',
      label: 'Pogodba o financiranju',
      value: settings?.napSiReceivingAgreementName || '—',
    },
  ];

  const fetchCompanies = async (financedId?: string): Promise<FinancingOption[]> => {
    if (!financedId) {
      return [];
    }
    const response = await Api.charge.getNapSiReceivingSupportCompanies(financedId);
    return response.data.data || [];
  };

  const fetchAgreements = async (
    financedId?: string,
    companyId?: string,
  ): Promise<FinancingOption[]> => {
    if (!financedId || !companyId) {
      return [];
    }
    const response = await Api.charge.getNapSiReceivingAgreements(financedId, companyId);
    return response.data.data || [];
  };

  const confirmEdit = async (values: any) => {
    // A cleared parent nulls its dependants. The service enforces this too, but sending
    // it explicitly keeps the request self-describing rather than relying on the server
    // to infer intent from a half-filled payload.
    const financedId = values.napSiFinancedId || null;
    const companyId = financedId ? values.napSiReceivingSupportCompanyId || null : null;
    const agreementId = companyId ? values.napSiReceivingAgreementId || null : null;

    const payload = {
      napSiFinancedId: financedId,
      napSiReceivingSupportCompanyId: companyId,
      napSiReceivingAgreementId: agreementId,
    };

    // One code path: clearing Nacin sends three nulls, and the service removes the
    // stored row rather than keeping an all-null one.
    await Api.charge.upsertChargePointNapSiSettings(chargePointId, payload);

    await loadSettings();
  };

  const onClickEdit = async () => {
    const current = settings || {};

    // Levels below Nacin are only prefetched when the stored row already reaches them,
    // so adding financing from scratch costs a single request.
    const [financeRecords, companyOptions, agreementOptions] = await Promise.all([
      Api.charge.getNapSiFinanceRecords().then((r: any) => r.data.data || []),
      fetchCompanies(current.napSiFinancedId),
      fetchAgreements(current.napSiFinancedId, current.napSiReceivingSupportCompanyId),
    ]);

    const inputs = [
      {
        name: 'napSiFinancedId',
        label: 'Način financiranja',
        type: FormInputTypes.select,
        options: withClear(financeRecords),
        // Changing Nacin invalidates both children, exactly as the NAP SI portal does.
        inputChangeCallback: async (value: string) => {
          drawerStore.setValues({
            ...drawerStore.values,
            napSiFinancedId: value,
            napSiReceivingSupportCompanyId: '',
            napSiReceivingAgreementId: '',
          });
          drawerStore.updateInputOptions('napSiReceivingAgreementId', []);
          drawerStore.updateInputOptions(
            'napSiReceivingSupportCompanyId',
            value ? withClear(await fetchCompanies(value)) : [],
          );
        },
      },
      {
        name: 'napSiReceivingSupportCompanyId',
        label: 'Prejemnik financiranja',
        type: FormInputTypes.select,
        options: withClear(companyOptions),
        condition: (values: any) => !!values.napSiFinancedId,
        inputChangeCallback: async (value: string) => {
          drawerStore.setValues({
            ...drawerStore.values,
            napSiReceivingSupportCompanyId: value,
            napSiReceivingAgreementId: '',
          });
          drawerStore.updateInputOptions(
            'napSiReceivingAgreementId',
            value
              ? withClear(await fetchAgreements(drawerStore.values.napSiFinancedId, value))
              : [],
          );
        },
      },
      {
        name: 'napSiReceivingAgreementId',
        label: 'Pogodba o financiranju',
        type: FormInputTypes.select,
        options: withClear(agreementOptions),
        condition: (values: any) =>
          !!values.napSiFinancedId && !!values.napSiReceivingSupportCompanyId,
      },
    ];

    drawerStore.openDrawer({
      label: settings ? 'Uredi NAP financiranje' : 'Dodaj NAP financiranje',
      inputs,
      clickCallback: () => confirmEdit(toJS(drawerStore.values)),
    });

    // Prefill so editing resolves the cascade from the stored Nacin rather than
    // starting empty.
    drawerStore.setValues({
      napSiFinancedId: current.napSiFinancedId || '',
      napSiReceivingSupportCompanyId: current.napSiReceivingSupportCompanyId || '',
      napSiReceivingAgreementId: current.napSiReceivingAgreementId || '',
    });
  };

  return (
    <StyledCard>
      <StyledCardHeader
        title="NAP Financiranje"
        onClick={onClickEdit}
        headerButtonIcon={settings ? HeaderButtonIcon.edit : HeaderButtonIcon.add}
      />
      <StyledCardContent>
        <NameValueRowList rows={rows} values={settings || {}} />
      </StyledCardContent>

      <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)}>
        <DialogContent>
          <Typography>
            Remove NAP financing for this charge point? The stored record is deleted, and the next
            sync stops sending financing for it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemoveOpen(false)}>Cancel</Button>
          <Button onClick={onConfirmRemove} autoFocus color="primary">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </StyledCard>
  );
});

export default ChargerNapSiSettingsCard;
